import { TIMEOUT } from '../constants';

/**
 * Custom error class for request timeouts.
 * Distinguishable via instanceof checks for retry logic.
 */
export class TimeoutError extends Error {
  public readonly timeout: number;
  public readonly url: string;

  constructor(url: string, timeout: number) {
    super(`Request to ${url} timed out after ${timeout}ms`);
    this.name = 'TimeoutError';
    this.timeout = timeout;
    this.url = url;
  }
}

export interface FetchWithTimeoutOptions extends RequestInit {
  timeout?: number;
}

/**
 * Fetch wrapper with AbortController-based timeout.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options including optional timeout
 * @param defaultTimeout - Default timeout if not specified in options
 * @returns Promise<Response>
 * @throws TimeoutError if request exceeds timeout
 * @throws Error for other fetch failures
 */
export async function fetchWithTimeout(
  url: string,
  options: FetchWithTimeoutOptions = {},
  defaultTimeout: number = TIMEOUT.DEFAULT_MS
): Promise<Response> {
  const { timeout = defaultTimeout, signal: externalSignal, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Link external signal if provided
  const abortHandler = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', abortHandler);
    }
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // Check if it was external abort vs timeout
      if (externalSignal?.aborted) {
        throw error; // Re-throw as AbortError for manual cancellation
      }
      console.warn(`[Timeout] Request to ${url} timed out after ${timeout}ms`);
      throw new TimeoutError(url, timeout);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) {
      externalSignal.removeEventListener('abort', abortHandler);
    }
  }
}
